import styled from '@emotion/styled';
import { Button, Checkbox, Dropdown, Input, Menu, message, Modal, ModalProps, Select } from 'antd';
import { useTranslate } from '@tolgee/react';
import {
  OperateValueContext,
  OperateValueProvider,
} from '@knowledge/knowledges/singleManage/context';
import FilterBar from '@knowledge/knowledges/singleClass/FilterBar';
import { cloneElement, useContext, useEffect, useMemo, useRef, useState } from 'react';
import Icon, { ArrowsAltOutlined, ShrinkOutlined } from '@ant-design/icons';
import Empty from 'components/Empty';
import { Item } from '@knowledge/knowledges/singleManage/GridView';
import {
  UnConfigSkuItem,
  FilterBarValueContext,
  CurrentKnowledgeValueContext,
  ConfigSkuItem,
  SpuClassDirtyContext,
  ClassActiveKnowledgeIdContext,
  SyncCheckboxSelect,
  CloseEditSkuButtonContext,
} from '@knowledge/knowledges/singleClass/context';
import { mergeItems, splitItems, useSelectControl } from '@knowledge/common/helpers/sku';
import { searchInItemScope } from '@knowledge/common/helpers/search';
import {
  segments2text,
  useIsEmptyValues,
  useValueName,
} from '@knowledge/knowledges/singleClass/helpers';
import { useField, useFormikContext } from 'formik';
import { useUpdateOneKnowledgeInKcList } from '@knowledge/knowledges/singleClass/connect';
import { cloneDeep, get, set, map, findIndex } from 'lodash-es';
import { getSpuInfoBySelectMap, SpuSelectMap } from '@knowledge/common/helpers/sku';
import { transformItemV2Spu2KnowledgeSpu } from '@knowledge/knowledges/singleClass/helpers';
import { cx } from '@emotion/css';
import { useGetSegmentsValue } from '@knowledge/common/share';
import { Virtuoso } from 'react-virtuoso';
import { css } from '@emotion/css';
import useModal from 'hooks/useModal';
import { ReactComponent as MultiSvg } from '@knowledge/assets/multi.svg';
import { TextAreaRef } from 'antd/lib/input/TextArea';
import insertAtCursor from 'utils/insertAtCursor';
import isEqual from 'react-fast-compare';
import { useLatest } from 'ahooks';

type OnSelect = (
  value:
    | {
        type: 'skus';
        skus: { internalItemId: string; skuId: string }[];
      }
    | {
        type: 'spus';
        ids: string[];
      },
) => void;

const Operate = ({
  multSelet = true,
  items,
  onSelect,
}: {
  multSelet?: boolean;
  items: ItemSelectV2.Item[];
  onSelect: OnSelect;
}) => {
  const { foldState, setFoldState } = useContext(OperateValueContext);
  const { t } = useTranslate();
  const { visible, show, hide } = useModal();
  return (
    <div
      style={{ flex: '0 0 auto' }}
      className="flex items-center text-[var(--fett-text-secondary)] hover:text-[#3366ff]"
    >
      {multSelet && (
        <div className="cursor-pointer mr-[12px] hover:text-[#3366ff]" onClick={show}>
          <Icon component={MultiSvg} className="!hover:text-[#3366ff]" />
          <span className="ml-[4px]">{t('knowledge.item.select.mode.mult')}</span>
        </div>
      )}
      {visible && (
        <SkuSelectModal visible={visible} onCancel={hide} items={items} onSelect={onSelect} />
      )}
      {foldState === 'unfold' ? (
        <div className="cursor-pointer" onClick={() => setFoldState('fold')}>
          <ShrinkOutlined className="mr-[4px]" />
          {t('single_fold_sku')}
        </div>
      ) : (
        <div className="cursor-pointer" onClick={() => setFoldState('unfold')}>
          <ArrowsAltOutlined className="mr-[4px]" />
          {t('single_unfold_sku')}
        </div>
      )}
    </div>
  );
};

export function SkuSelectModal({
  items,
  onSelect,
  onCancel,
  ...otherProps
}: ModalProps & {
  items: ItemSelectV2.Item[];
  onSelect: OnSelect;
}) {
  const { t } = useTranslate();
  const typeLabelMap = {
    4: t('sider.SKU货号'),
    2: t('sider.商品ID'),
    3: t('aide.主商品货号'),
  };
  const options = Object.entries(typeLabelMap).map(([value, label]) => {
    return { value: parseInt(value, 10), label };
  });
  const typePlaceholderMap = {
    2: t('knowledge.item.quickMode', { x: t('sider.商品ID') }),
    3: t('knowledge.item.quickMode', { x: t('aide.主商品货号') }),
    4: t('knowledge.item.quickMode', { x: t('sider.SKU货号') }),
  };
  const textareaRef = useRef<TextAreaRef>(null);
  type Type = 2 | 3 | 4;
  const [selectType, setSelectType] = useState<Type>(4);
  const [text, setText] = useState('');
  const onPaste = () => {
    navigator.clipboard
      .readText()
      .then((text) => {
        const { current: textarea } = textareaRef;

        if (textarea && text) {
          const { resizableTextArea } = textarea;

          if (resizableTextArea) {
            const { textArea } = resizableTextArea;

            const nextValue = insertAtCursor(textArea, text);

            setText(nextValue);
          }
        }
      })
      .catch(() => {});
  };

  return (
    <Modal
      title={t('knowledge.item.select.mode.mult')}
      width={600}
      onCancel={onCancel}
      okText={t('knowledge.item.select.mode.mult')}
      okButtonProps={{
        disabled: !text.trim(),
        onClick: (e) => {
          const keywords = text
            .replaceAll(/,|，/g, '\n')
            .split('\n')
            .map((_) => _.trim())
            .filter((_) => _);
          const foundMap: Record<string, boolean> = {};
          const skus =
            selectType === 4
              ? keywords.reduce<{ internalItemId: string; skuId: string }[]>((res, keyword) => {
                  const lowerCaseKeyword = keyword.toLowerCase();
                  return items.reduce((res, { internalItemId, skuInfo }) => {
                    return skuInfo.reduce((res, { sellerSku, skuId }) => {
                      if (sellerSku.toLowerCase().includes(lowerCaseKeyword)) {
                        const sku = { internalItemId, skuId };
                        if (res.every((_) => !isEqual(_, sku))) {
                          res.push(sku);
                        }
                        foundMap[keyword] = true;
                      }
                      return res;
                    }, res);
                  }, res);
                }, [])
              : [];
          const ids =
            selectType !== 4
              ? keywords.reduce<string[]>((res, keyword) => {
                  const lowerCaseKeyword = keyword.toLowerCase();
                  return items.reduce((res, { internalItemId, externalItemId, itemSku }) => {
                    if (
                      (selectType === 2 && keyword === externalItemId) ||
                      (selectType === 3 && itemSku.toLowerCase().includes(lowerCaseKeyword))
                    ) {
                      if (!res.includes(internalItemId)) {
                        res.push(internalItemId);
                      }
                      foundMap[keyword] = true;
                    }
                    return res;
                  }, res);
                }, [])
              : [];
          const notFoundKeywords = keywords.filter((keyword) => !foundMap[keyword]);
          if (notFoundKeywords.length) {
            message.warn(t('aide.x项未找到对应商品', { x: notFoundKeywords.length }));
            setText(notFoundKeywords.join('\n'));
          } else {
            message.success(
              t('aide.已选中x项', { x: selectType === 4 ? skus.length : ids.length }),
            );
            setText('');
            onCancel?.(e);
          }
          if (selectType === 4 && skus.length) {
            onSelect({
              type: 'skus',
              skus,
            });
          }
          if (selectType !== 4 && ids.length) {
            onSelect({
              type: 'spus',
              ids,
            });
          }
        },
      }}
      {...otherProps}
    >
      <div className="pb-[16px]">
        <span>{t('aide.选择类型')}: </span>
        <Select
          dropdownMatchSelectWidth={false}
          value={selectType}
          onChange={setSelectType}
          options={options}
        />
      </div>
      <div className="">
        <div className="">
          <div className="">
            <Dropdown
              trigger={['contextMenu']}
              overlay={
                <Menu>
                  <Menu.Item onClick={onPaste}>{t('ViewsChat.粘贴')}</Menu.Item>
                </Menu>
              }
            >
              <Input.TextArea
                autoSize={{ minRows: 12 }}
                ref={textareaRef}
                placeholder={
                  selectType === 4 ? t('select-sku-tips') : typePlaceholderMap[selectType]
                }
                value={text}
                onChange={({ target: { value } }) => setText(value)}
              />
            </Dropdown>
          </div>
        </div>
      </div>
    </Modal>
  );
}

type SpuSelectProps = {
  selectable?: boolean;
  listOnly?: boolean;
  spus: ItemSelectV2.Item[];
  defaultSpuSelectMap?: SpuSelectMap;
  [k: string]: any;
  children?: React.ReactElement;
  type: string;
};

export const SpuSelect = (props: Omit<SpuSelectProps, 'spus' | 'defaultSpuSelectMap'>) => {
  const spus = useContext(UnConfigSkuItem);
  const { data, isParent } = useContext(SyncCheckboxSelect);
  return (
    <SpuSelectInner
      {...props}
      spus={spus}
      defaultSpuSelectMap={isParent ? undefined : data['unConfig']}
      type="unConfig"
    />
  );
};

export const SpuPreview = (props: Omit<SpuSelectProps, 'spus' | 'defaultSpuSelectMap'>) => {
  const spus = useContext(ConfigSkuItem);
  const { data, isParent } = useContext(SyncCheckboxSelect);
  return (
    <SpuSelectInner
      {...props}
      spus={spus}
      defaultSpuSelectMap={isParent ? undefined : data['config']}
      type="config"
    />
  );
};

export const SpuSelectInner = (props: SpuSelectProps) => {
  const { selectable = true, listOnly, spus, children, type, defaultSpuSelectMap } = props;
  const { t } = useTranslate();
  const [filterSpus, setFilterSpus] = useState<ItemSelectV2.Item[]>(spus);
  const filterSpusIds = filterSpus.map((spu) => spu.internalItemId);
  const [
    spuSelectMap,
    selectedSkuLen,
    { toggleSku, toggleSkus, toggleSpu, toggleBatch, getBatchStatus },
  ] = useSelectControl(spus, defaultSpuSelectMap);
  const { checked, indeterminate } = useMemo(() => {
    return getBatchStatus(filterSpusIds);
  }, [filterSpusIds, getBatchStatus]);
  const { storeScopes, keyword, keywordType } = useContext(FilterBarValueContext);
  useEffect(() => {
    searchInItemScope(spus, {
      storeScopes,
      keyword,
      keywordType,
      name: keywordType === 1 ? keyword : '',
    }).then((res) => {
      setFilterSpus(res);
    });
  }, [spus, storeScopes, keyword, keywordType]);
  const [field] = useField('knowledgeLevel');
  const { registerDirty } = useContext(SpuClassDirtyContext);
  const { activeId } = useContext(ClassActiveKnowledgeIdContext);
  const activeIdRef = useLatest(activeId);
  useEffect(() => {
    registerDirty(`${activeIdRef.current}-${type}`, selectedSkuLen > 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSkuLen, registerDirty, type]);

  return (
    <OperateValueProvider level={field.value}>
      <div className="h-full flex flex-col overflow-hidden">
        {!listOnly && (
          <div className="basis-[56px] flex-shrink-0 px-[16px] py-[12px] flex items-center justify-between">
            <FilterBar />
            <Operate
              multSelet={selectable}
              items={filterSpus}
              onSelect={(value) => {
                if (value.type === 'skus') {
                  toggleSkus(true, value.skus);
                }
                if (value.type === 'spus') {
                  toggleBatch(value.ids, true);
                }
              }}
            />
          </div>
        )}

        {filterSpus?.length === 0 ? (
          <div className="h-full flex items-center justify-center">
            <Empty description={t('storeSelector.暂无数据')} />
          </div>
        ) : (
          <div className="flex-grow px-[16px] flex flex-col overflow-hidden">
            {selectable && (
              <div className="basis-[27px] pt-[1px] pb-[6px] h-[20px] flex-shrink-0 flex items-center">
                <Checkbox
                  checked={checked}
                  indeterminate={indeterminate}
                  onChange={(e) => {
                    toggleBatch(filterSpusIds, e.target.checked);
                  }}
                >
                  {t('marketing.全选')}
                </Checkbox>
              </div>
            )}

            <VirtualList
              selectable={selectable}
              filterSpus={filterSpus}
              toggleSku={toggleSku}
              toggleSpu={toggleSpu}
              spuSelectMap={spuSelectMap}
            />
          </div>
        )}

        {children && (
          <Footer
            className={cx(
              'flex-shrink-0 bg-[var(--fett-bg-panel)]',
              children?.props?.drawer ? '' : 'basis-[56px]',
            )}
          >
            {cloneElement(children, {
              selectedSkuLen,
              spuSelectMap,
              spus,
            })}
          </Footer>
        )}
      </div>
    </OperateValueProvider>
  );
};

export const VirtualList = (props: {
  selectable?: boolean;
  filterSpus: ItemSelectV2.Item[];
  toggleSku: any;
  toggleSpu: any;
  spuSelectMap: any;
}) => {
  const { selectable = true, filterSpus, toggleSku, toggleSpu, spuSelectMap } = props;

  return (
    <div className="h-full flex-grow pb-[12px]">
      <Virtuoso
        className={css`
          & > div {
            & > div {
              & > div {
                box-sizing: border-box;
                background: var(--fett-bg-panel);
                margin-bottom: 12px;
                border: 1px solid var(--fett-border-strong);
                border-radius: 2px;
                padding: 10px 13px 12px 8px;
              }
            }
          }
        `}
        data={filterSpus}
        itemContent={(_, data) => {
          return (
            <Item
              selectable={selectable}
              selectedInfo={spuSelectMap[data.internalItemId] || {}}
              toggleSku={toggleSku}
              toggleSpu={toggleSpu}
              item={data}
              key={data.internalItemId}
            />
          );
        }}
      />
    </div>
  );
};

export const UnConfigFooter = (props: {
  knowledgeName: string;
  selectedSkuLen?: number;
  spuSelectMap?: SpuSelectMap;
  spus?: ItemSelectV2.Item[];
  drawer?: boolean;
}) => {
  const { selectedSkuLen, spus, spuSelectMap, knowledgeName, drawer } = props;
  const { t } = useTranslate();
  const valueName = useValueName();
  const { values, setValues } = useFormikContext<Knowledge.SingleClass.Knowledge>();
  const { active } = useContext(CurrentKnowledgeValueContext);
  const isEmptyValue = useIsEmptyValues();
  const { update, loading } = useUpdateOneKnowledgeInKcList();
  const { syncMap } = useContext(SyncCheckboxSelect);
  const { onClose } = useContext(CloseEditSkuButtonContext);
  useEffect(() => {
    syncMap('unConfig', spuSelectMap!);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spuSelectMap]);

  const node1 = selectedSkuLen ? (
    <div
      title={t('knowledge.选中xadd', {
        x: selectedSkuLen,
        y: knowledgeName,
        z: valueName,
      })}
      className={cx(
        'text-[12px] text-[var(--fett-text-tertiary)] line-clamp-2',
        drawer ? 'ml-[16px]' : 'pl-[8px] mr-[16px]',
      )}
    >
      {t('knowledge.选中xadd', {
        x: selectedSkuLen,
        y: knowledgeName,
        z: valueName,
      })}
    </div>
  ) : null;

  const node2 = (
    <Button
      loading={loading}
      onClick={() => {
        if (isEmptyValue) {
          return message.warning(t('knowledge.请先添加知识值'));
        }
        const copy = cloneDeep(values);
        const result = getSpuInfoBySelectMap(spus!, spuSelectMap!).map((e) =>
          transformItemV2Spu2KnowledgeSpu(e),
        );
        const index = findIndex(copy.values, { id: active });
        set(
          copy,
          `values.${index}.spus`,
          mergeItems(get(values, `values.${index}.spus`, []), result),
        );
        update(copy).then(() => {
          // 保存成功后立即把最新表单状态写回，未配置列表（基于实时 values 派生）同步移除已配置项，
          // 避免依赖 enableReinitialize 的二次服务端回灌带来的列表抖动/多出新商品。
          setValues(copy);
          message.success(t('operation.saveSucceed'));
        });
      }}
      disabled={selectedSkuLen === 0}
      type="primary"
      className="mr-[16px]"
    >
      {t('phrase.确定')}
    </Button>
  );

  const node3 = <Button onClick={onClose}>{t('translation.取消')}</Button>;

  return drawer ? (
    <div className="flex items-center justify-start h-full">
      {node2}
      {node3}
      {node1}
    </div>
  ) : (
    <div className="flex items-center justify-end h-full">
      {node1}
      {node2}
    </div>
  );
};

export const ConfigFooter = (props: {
  citeMode?: boolean;
  selectedSkuLen?: number;
  spuSelectMap?: SpuSelectMap;
  spus?: ItemSelectV2.Item[];
  drawer?: boolean;
}) => {
  const { citeMode, selectedSkuLen, spus, spuSelectMap, drawer } = props;
  const { t } = useTranslate();
  const { values, setValues, handleSubmit } = useFormikContext<Knowledge.SingleClass.Knowledge>();
  const { active } = useContext(CurrentKnowledgeValueContext);
  const { update, loading } = useUpdateOneKnowledgeInKcList();
  const [tempValues, setTempValues] = useState(undefined);
  const { syncMap } = useContext(SyncCheckboxSelect);
  const { getSegmentsValue } = useGetSegmentsValue();
  useEffect(() => {
    syncMap('config', spuSelectMap!);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spuSelectMap]);
  const { onClose } = useContext(CloseEditSkuButtonContext);

  const node1 = selectedSkuLen ? (
    <div
      className={cx(
        'text-[12px] text-[var(--fett-text-tertiary)]',
        drawer ? 'ml-[16px]' : 'mr-[16px]',
      )}
    >
      {t('voucher.已选x', { x: selectedSkuLen })} SKU
    </div>
  ) : null;

  const node2 = (
    <>
      <Select
        className="!mr-[16px]"
        disabled={selectedSkuLen === 0}
        placeholder={t('single_move_to_another_kc')}
        value={tempValues || null}
        onChange={(value) => {
          if (!citeMode && value) {
            setTempValues(value);
          }
          const copy = cloneDeep(values);
          const toIndex = copy.values.findIndex((e) => e.id === value);
          set(
            copy,
            `values.${toIndex}.spus`,
            mergeItems(
              get(values, `values.${toIndex}.spus`, []),
              getSpuInfoBySelectMap(spus!, spuSelectMap!).map((e) =>
                transformItemV2Spu2KnowledgeSpu(e),
              ),
            ),
          );
          const index = findIndex(copy.values, { id: active });
          set(
            copy,
            `values.${index}.spus`,
            splitItems(
              get(values, `values.${index}.spus`, []),
              getSpuInfoBySelectMap(spus!, spuSelectMap!).map((e) =>
                transformItemV2Spu2KnowledgeSpu(e),
              ),
            ),
          );
          if (citeMode) {
            setValues(copy);
            handleSubmit();
            setTempValues(undefined);
          } else {
            update(copy)
              .then(() => {
                message.success(t('single_has_moved'));
              })
              .finally(() => {
                setTempValues(undefined);
              });
          }
        }}
      >
        {values.values.map((item) => {
          if (item.id === active) {
            return null;
          }
          const content = segments2text(t, getSegmentsValue(item));
          return (
            <Select.Option key={item.id} value={item.id}>
              {content}
            </Select.Option>
          );
        })}
      </Select>
      <Button
        loading={loading}
        onClick={() => {
          const copy = cloneDeep(values);
          const result = getSpuInfoBySelectMap(spus!, spuSelectMap!).map((e) =>
            transformItemV2Spu2KnowledgeSpu(e),
          );
          const index = findIndex(copy.values, { id: active });
          set(
            copy,
            `values.${index}.spus`,
            splitItems(map(spus!, transformItemV2Spu2KnowledgeSpu), result),
          );
          if (citeMode) {
            setValues(copy);
            handleSubmit();
          } else {
            update(copy).then(() => {
              message.success(t('single_has_removed'));
            });
          }
        }}
        className="mr-[16px]"
        type="primary"
        disabled={selectedSkuLen === 0}
      >
        {t('single_remove')}
      </Button>
    </>
  );
  const node3 = <Button onClick={onClose}>{t('translation.取消')}</Button>;
  return drawer ? (
    <div className="flex items-center justify-start h-full">
      {node2}
      {node3}
      {node1}
    </div>
  ) : (
    <div className="flex items-center justify-end h-full">
      {node1}
      {node2}
    </div>
  );
};

const Footer = styled.div`
  box-shadow: 0px -2px 8px 0px rgba(167, 178, 204, 0.1),
    inset 0px 1px 0px 0px var(--fett-border-default);
`;
